import React from 'react';

import { Button } from '@travelnest/merlin.button';
import { Modal } from '@travelnest/merlin.modal';
import { RadioQuestion } from '@travelnest/merlin.radio';
import { Spacer } from '@travelnest/merlin.spacer';
import theme from '@travelnest/merlin.theme';
import { IEventNote, IPendingEventNote } from 'data/entities/event/event-note';
import { Paragraph } from '../../../../../components/paragraph/Paragraph';

type CancellationSource = 'internal' | 'owner' | 'other';
const cancellationSourceDescription = (source: CancellationSource) => {
	switch (source) {
		case 'owner':
			return 'Requested by Owner';
		case 'internal':
			return 'Requested by Travelnest';
		case 'other':
			return 'Other';
		default:
			throw new Error(`Unknown cancellaton source: ${source}`);
	}
};

interface ICancellationRequestModalProps {
	propertyId: number | string;
	eventId: string;
	onRequestClose: () => any;
	submitEventNotes: (propertyId: string | number, eventId: string, eventNotes: IPendingEventNote[]) => any;
}

export const CancellationRequestModal: React.FC<ICancellationRequestModalProps> = (props) => {
	const { propertyId, eventId, onRequestClose, submitEventNotes } = props;
	const [cancellationSource, setCancellationSource] = React.useState<CancellationSource | undefined>(undefined);

	const onSave = async () => {
		if (cancellationSource === undefined) {
			throw new Error('Cancellation source undefined');
		}

		const noteCreatePayload = {
			action: 'add',
			eventNote: {
				event_id: eventId,
				note_type: 'audit',
				content: `Booking cancellation has been requested: ${cancellationSourceDescription(cancellationSource)}\nCode: cancellation_request/${cancellationSource}`
			} as IEventNote,
			eventId
		} as IPendingEventNote;

		await submitEventNotes(propertyId, eventId, [noteCreatePayload]);

		onRequestClose();
	};

	return <Modal
		title="Cancellation request"
		shouldCloseOnOverlayClick={false}
		onRequestClose={onRequestClose}
		actionRHS={
			<Button
				id="booking_cancellation_request_save"
				onClick={onSave}
				bgColor={theme.colours.success.default}
				color={theme.colours.text.light}
				disabled={cancellationSource === undefined}
			>
				Save
			</Button>}
	>
		<div className="request">
			<Paragraph>
				Submit a cancellation request for this booking.
			</Paragraph>
			<Spacer />
			<RadioQuestion
				id={'cancellation-source'}
				question={'Who is initiating the booking cancellation?'}
				options={{
					[cancellationSourceDescription('internal')]: 'internal',
					[cancellationSourceDescription('owner')]: 'owner',
					[cancellationSourceDescription('other')]: 'other'
				}}
				selected={cancellationSource}
				onChange={(_, value) => setCancellationSource(value)}
			/>
		</div>
	</Modal>;
};
